<template>
  <div class="product-list-page">
    <div class="page-header">
      <button class="back-btn" @click="$router.back()">‹ 返回</button>
      <div class="page-title">{{ pageTitle }}</div>
    </div>

    <div class="toolbar">
      <div v-if="hasSearch" class="search-box">
        <input
          v-model="keyword"
          class="search-input"
          type="text"
          placeholder="搜索关键词"
          @keyup.enter="onSearch"
        />
        <button class="search-btn" @click="onSearch">搜索</button>
      </div>
      <div class="toolbar-actions">
        <div class="toolbar-btn toolbar-btn--filter" :class="{ active: filterActiveCount > 0 }" @click="showFilterDrawer = true">
          筛选<span v-if="filterActiveCount > 0" class="toolbar-badge">{{ filterActiveCount }}</span>
        </div>
        <div class="toolbar-btn" :class="{ active: sortBy !== 'default' }" @click="showSortSheet = true">
          {{ sortLabel }}
        </div>
      </div>
    </div>

    <div v-if="filterGroups.length > 0" class="filter-groups">
      <div v-for="(group, groupIndex) in filterGroups" :key="groupIndex" class="filter-group-row">
        <div
          v-for="value in group.values"
          :key="value.id"
          class="filter-group-tag"
          :class="{ active: group.activeId === value.id }"
          @click="selectFilterGroupValue(groupIndex, value.id)"
        >{{ value.name }}</div>
      </div>
    </div>

    <div v-if="chips.length > 0" class="chips-row">
      <div v-for="chip in chips" :key="chip.key" class="chip" @click="removeChip(chip.key)">
        {{ chip.label }}<span class="chip-close">×</span>
      </div>
      <div class="chip chip-reset" @click="clearAllChips">全部清除</div>
    </div>

    <div class="product-list-container">
      <div v-if="products.length > 0" class="products-grid">
        <product-card
          v-for="product in products"
          :key="product.id"
          layout="grid"
          :product="product"
          @click="goToProductDetail"
        />
      </div>
      <empty-state
        v-else-if="!loading"
        title="暂无产品"
        description="该分类下暂无产品"
        emoji="🎫"
        :show-button="true"
        button-text="返回首页"
        @click="goToHome"
      />
      <div v-if="loading" class="loading-text">加载中...</div>
      <div v-if="!hasMore && products.length > 0" class="no-more-text">没有更多了</div>
    </div>
    <div style="height:80px"></div>

    <!-- 筛选抽屉 -->
    <div v-if="showFilterDrawer" class="modal-overlay" @click.self="showFilterDrawer = false">
      <div class="modal-sheet">
        <div class="modal-header">
          <div class="modal-title">筛选</div>
          <button class="modal-close" @click="showFilterDrawer = false">✕</button>
        </div>

        <div class="filter-section">
          <div class="filter-section-title">价格区间</div>
          <div class="price-range-row">
            <input v-model="draftPriceMin" class="price-input" type="number" min="0" placeholder="最低价" />
            <span class="price-sep">-</span>
            <input v-model="draftPriceMax" class="price-input" type="number" min="0" placeholder="最高价" />
          </div>
        </div>

        <div class="filter-section">
          <div class="filter-section-title">行程天数</div>
          <div class="option-tags">
            <div
              v-for="(preset, idx) in filterOptions.dayPresets"
              :key="idx"
              class="option-tag"
              :class="{ active: draftDayTags.includes(idx) }"
              @click="toggleDraftDayTag(idx)"
            >{{ preset.label }}</div>
          </div>
        </div>

        <div v-if="dictTypeOptions.length > 0" class="filter-section">
          <div class="filter-section-title">产品类型</div>
          <div class="option-tags">
            <div
              v-for="opt in dictTypeOptions"
              :key="opt.id"
              class="option-tag"
              :class="{ active: draftDictDataIds.includes(opt.id) }"
              @click="toggleDraftDictDataId(opt.id)"
            >{{ opt.label }}</div>
          </div>
        </div>

        <div v-if="filterOptions.cities.length > 0" class="filter-section">
          <div class="filter-section-title">城市</div>
          <div class="option-tags">
            <div
              v-for="city in filterOptions.cities"
              :key="city"
              class="option-tag"
              :class="{ active: draftCities.includes(city) }"
              @click="toggleDraftCity(city)"
            >{{ city }}</div>
          </div>
        </div>

        <div class="drawer-footer">
          <button class="drawer-btn drawer-btn--reset" @click="resetDraftFilters">重置</button>
          <button class="drawer-btn drawer-btn--confirm" @click="applyDraftFilters">确定</button>
        </div>
      </div>
    </div>

    <!-- 排序弹层 -->
    <div v-if="showSortSheet" class="modal-overlay" @click.self="showSortSheet = false">
      <div class="modal-sheet">
        <div class="modal-header">
          <div class="modal-title">排序方式</div>
          <button class="modal-close" @click="showSortSheet = false">✕</button>
        </div>
        <div class="picker-list">
          <div
            v-for="opt in sortOptions"
            :key="opt.value"
            class="picker-item"
            :class="{ active: sortBy === opt.value }"
            @click="selectSort(opt.value)"
          >{{ opt.label }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getProductList, getPageFull, getProductFilterOptions, getDictByCode } from '@/api/browse'
import ProductCard from '@/components/product-card/product-card.vue'
import EmptyState from '@/components/empty-state/empty-state.vue'

const PRICE_DEFAULT_MAX = 1000

const SORT_OPTIONS = [
  { value: 'default', label: '默认排序' },
  { value: 'price_asc', label: '价格从低到高' },
  { value: 'price_desc', label: '价格从高到低' },
  { value: 'newest', label: '最新上架' }
]

function toIntOrNull(v) {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  if (!isFinite(n) || n < 0) return null
  return Math.floor(n)
}

// 与小程序 pages/list/list.js 的 normalizeFilterGroups 逻辑一致：
// 归一化后端 templateInfo.filters（id/value.id 后端返回的是字符串，统一转数字）
function normalizeFilterGroups(filters) {
  return (Array.isArray(filters) ? filters : []).map(group => {
    const values = (Array.isArray(group && group.values) ? group.values : [])
      .map(v => ({ id: Number(v && v.id) || 0, name: String((v && v.name) || '') }))
      .filter(v => v.id > 0 && v.name)
    return {
      name: String((group && group.name) || ''),
      values,
      activeId: values.length ? values[0].id : 0
    }
  }).filter(group => group.values.length > 0)
}

export default {
  name: 'ProductList',
  components: {
    ProductCard,
    EmptyState
  },
  data() {
    return {
      productTypeId: null,
      pageTitle: '产品列表',
      hasSearch: false,
      filterGroups: [],
      keyword: '',
      sortBy: 'default',
      sortOptions: SORT_OPTIONS,
      showSortSheet: false,
      showFilterDrawer: false,
      filterOptions: { cities: [], priceRange: { min: 0, max: PRICE_DEFAULT_MAX }, dayPresets: [] },
      dictTypeOptions: [],
      // 已应用的筛选条件
      priceMin: null,
      priceMax: null,
      dayTags: [],
      dictDataIds: [],
      cities: [],
      // 抽屉内编辑中的草稿（点"确定"才生效，与小程序交互一致）
      draftPriceMin: '',
      draftPriceMax: '',
      draftDayTags: [],
      draftDictDataIds: [],
      draftCities: [],
      products: [],
      pageIndex: 1,
      pageSize: 20,
      hasMore: true,
      loading: false,
      // 从"重置筛选/分页状态"到"loadProducts 首次真正发起请求"之间的过渡态，防止这段间隙里的
      // 滚动触底误判提前触发翻页（见 initFromRoute / handleScroll 注释）
      initializing: false
    }
  },
  computed: {
    sortLabel() {
      const opt = SORT_OPTIONS.find(o => o.value === this.sortBy)
      return opt && opt.value !== 'default' ? opt.label : '排序'
    },
    filterActiveCount() {
      let count = 0
      if (this.priceMin != null || this.priceMax != null) count++
      if (this.dayTags.length) count++
      if (this.dictDataIds.length) count++
      if (this.cities.length) count++
      return count
    },
    chips() {
      const chips = []
      if (this.priceMin != null || this.priceMax != null) {
        const mn = this.priceMin != null ? this.priceMin : 0
        const mx = this.priceMax
        chips.push({ key: 'price', label: mx != null ? `¥${mn} - ¥${mx}` : `¥${mn}以上` })
      }
      this.dayTags.forEach(idx => {
        const preset = this.filterOptions.dayPresets[idx]
        if (preset) chips.push({ key: `day:${idx}`, label: preset.label })
      })
      this.dictDataIds.forEach(id => {
        const opt = this.dictTypeOptions.find(o => o.id === id)
        chips.push({ key: `dict:${id}`, label: opt ? opt.label : `#${id}` })
      })
      this.cities.forEach(name => {
        chips.push({ key: `city:${name}`, label: name })
      })
      return chips
    }
  },
  watch: {
    '$route.params.id'() {
      this.initFromRoute()
    },
    showFilterDrawer(val) {
      if (val) this.openFilterDrawerDraft()
    }
  },
  mounted() {
    this.initFromRoute()
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    async initFromRoute() {
      const raw = this.$route.params.id
      this.productTypeId = raw ? Number(raw) : null
      // 重置筛选/分页状态，避免残留上一个分类的数据
      this.hasSearch = false
      this.filterGroups = []
      this.keyword = ''
      this.sortBy = 'default'
      this.priceMin = null
      this.priceMax = null
      this.dayTags = []
      this.dictDataIds = []
      this.cities = []
      this.pageIndex = 1
      this.hasMore = true
      this.products = []
      // 在真正发起首次请求之前先同步置为 initializing，避免"重置完成→首个请求还没发出"这段异步等待窗口内，
      // 路由切换导致页面变矮触发浏览器强制回收 scrollY（原生 scroll 事件），被 handleScroll 误判为"接近底部"
      // 从而提前把 pageIndex 加到 2（此时 loading 还是 false、hasMore 还是 true，两个判断都会通过）。
      // 用独立字段而非直接置 loading=true，是因为 loadProducts 自身用 loading 做防重入 short-circuit，
      // 提前置 loading 会导致后面真正的 loadProducts() 调用被自己的守卫拦掉。
      this.initializing = true

      this.loadFilterOptions()
      this.loadDictTypeOptions()
      // 必须等 templateInfo（决定 filterGroups 初始选中值）加载完成后才能发首次产品列表请求，
      // 否则 loadProducts 里算出的 dictDataIdIntersect 会先于 filterGroups 填充完成而漏掉快捷分类条件
      await this.loadNavTemplateInfo()
      this.loadProducts()
    },
    // 从首页页面配置里找到 linkValue 与当前 productTypeId 匹配的导航项，取其 templateInfo。
    // 找不到（未带 id / id 不存在 / 该项未配置模板）时保持默认（无搜索框、无快捷分类），不影响其他筛选功能。
    async loadNavTemplateInfo() {
      if (!this.productTypeId) return
      try {
        const res = await getPageFull()
        const sections = (res.data && res.data.sections) || []
        for (const section of sections) {
          if (section.position !== 'home_nav') continue
          for (const item of section.items || []) {
            if (Number(item.linkValue) === this.productTypeId) {
              this.pageTitle = item.configName || '产品列表'
              const templateInfo = item.templateInfo || {}
              this.hasSearch = !!templateInfo.hasSearch
              this.filterGroups = normalizeFilterGroups(templateInfo.filters)
              return
            }
          }
        }
      } catch (error) {
        console.error('加载导航配置失败:', error)
      }
    },
    async loadFilterOptions() {
      try {
        const res = await getProductFilterOptions()
        const data = res.data || {}
        this.filterOptions = {
          cities: data.cities || [],
          priceRange: data.priceRange || { min: 0, max: PRICE_DEFAULT_MAX },
          dayPresets: data.dayPresets || []
        }
      } catch (error) {
        console.error('加载筛选选项失败:', error)
      }
    },
    async loadDictTypeOptions() {
      try {
        const res = await getDictByCode('product_type')
        const list = res.data || []
        this.dictTypeOptions = list
          .filter(item => item.id !== this.productTypeId)
          .map(item => ({ id: item.id, label: item.label }))
      } catch (error) {
        console.error('加载产品类型失败:', error)
      }
    },
    // 滚动到距底部 200px 内时自动加载下一页。
    // initializing / products.length === 0 两个守卫专门防止"翻页跳过第一页"：
    // 路由切换到本页时（尤其是从更长的页面点进来），浏览器会把已经超出新页面高度的 scrollY
    // 强制回收到新的最大值，这个回收动作本身会触发一次原生 scroll 事件——发生在 initFromRoute
    // 重置完 pageIndex/products 之后、loadProducts 首次请求真正发出之前的这段 await 间隙里，
    // 如果没有额外guard，此时 loading 还是 false、hasMore 还是 true，会被误判成"已到底部"，
    // 提前把 pageIndex 从 1 加到 2，导致本该展示第 1 页却发出第 2 页请求（列表看起来是空的）。
    handleScroll() {
      if (this.loading || this.initializing || !this.hasMore || this.products.length === 0) return
      const scrollBottom = window.innerHeight + window.scrollY
      if (scrollBottom >= document.documentElement.scrollHeight - 200) {
        this.loadMore()
      }
    },
    onSearch() {
      this.pageIndex = 1
      this.hasMore = true
      this.products = []
      this.loadProducts()
    },
    selectFilterGroupValue(groupIndex, valueId) {
      const group = this.filterGroups[groupIndex]
      if (!group || group.activeId === valueId) return
      group.activeId = valueId
      this.pageIndex = 1
      this.hasMore = true
      this.products = []
      this.loadProducts()
    },
    selectSort(value) {
      this.sortBy = value
      this.showSortSheet = false
      this.pageIndex = 1
      this.hasMore = true
      this.products = []
      this.loadProducts()
    },
    openFilterDrawerDraft() {
      this.draftPriceMin = this.priceMin != null ? String(this.priceMin) : ''
      this.draftPriceMax = this.priceMax != null ? String(this.priceMax) : ''
      this.draftDayTags = [...this.dayTags]
      this.draftDictDataIds = [...this.dictDataIds]
      this.draftCities = [...this.cities]
    },
    toggleDraftDayTag(idx) {
      const at = this.draftDayTags.indexOf(idx)
      if (at >= 0) this.draftDayTags.splice(at, 1)
      else this.draftDayTags.push(idx)
    },
    toggleDraftDictDataId(id) {
      const at = this.draftDictDataIds.indexOf(id)
      if (at >= 0) this.draftDictDataIds.splice(at, 1)
      else this.draftDictDataIds.push(id)
    },
    toggleDraftCity(name) {
      const at = this.draftCities.indexOf(name)
      if (at >= 0) this.draftCities.splice(at, 1)
      else this.draftCities.push(name)
    },
    resetDraftFilters() {
      this.draftPriceMin = ''
      this.draftPriceMax = ''
      this.draftDayTags = []
      this.draftDictDataIds = []
      this.draftCities = []
    },
    applyDraftFilters() {
      this.priceMin = toIntOrNull(this.draftPriceMin)
      this.priceMax = toIntOrNull(this.draftPriceMax)
      this.dayTags = [...this.draftDayTags]
      this.dictDataIds = [...this.draftDictDataIds]
      this.cities = [...this.draftCities]
      this.showFilterDrawer = false
      this.pageIndex = 1
      this.hasMore = true
      this.products = []
      this.loadProducts()
    },
    removeChip(key) {
      if (key === 'price') {
        this.priceMin = null
        this.priceMax = null
      } else if (key.startsWith('day:')) {
        const idx = Number(key.slice(4))
        this.dayTags = this.dayTags.filter(d => d !== idx)
      } else if (key.startsWith('dict:')) {
        const id = Number(key.slice(5))
        this.dictDataIds = this.dictDataIds.filter(d => d !== id)
      } else if (key.startsWith('city:')) {
        const name = key.slice(5)
        this.cities = this.cities.filter(c => c !== name)
      }
      this.pageIndex = 1
      this.hasMore = true
      this.products = []
      this.loadProducts()
    },
    clearAllChips() {
      this.priceMin = null
      this.priceMax = null
      this.dayTags = []
      this.dictDataIds = []
      this.cities = []
      this.pageIndex = 1
      this.hasMore = true
      this.products = []
      this.loadProducts()
    },
    async loadProducts() {
      if (this.loading) return
      this.loading = true
      // 真正的请求已经发出，initFromRoute 里为堵住时间窗口而设的过渡态可以解除了
      this.initializing = false
      try {
        // 天数分档转换为最终的 minDays/maxDays（多选取并集范围，任一档无上限则整体不设上限，与小程序逻辑一致）
        let minDays = null
        let maxDays = null
        let noMaxDaysCap = false
        this.dayTags.forEach(idx => {
          const preset = this.filterOptions.dayPresets[idx]
          if (!preset) return
          if (minDays == null || preset.minDays < minDays) minDays = preset.minDays
          if (preset.maxDays == null) noMaxDaysCap = true
          else if (maxDays == null || preset.maxDays > maxDays) maxDays = preset.maxDays
        })
        if (noMaxDaysCap) maxDays = null

        const configuredIntersections = this.filterGroups
          .map(group => Number(group.activeId) || 0)
          .filter(id => id > 0)

        const body = {
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
          keyword: this.keyword || null,
          dictDataId: this.dictDataIds.length
            ? [...this.dictDataIds, ...(this.productTypeId ? [this.productTypeId] : [])]
            : (this.productTypeId ? [this.productTypeId] : null),
          dictDataIdIntersect: configuredIntersections.length ? configuredIntersections : null,
          cities: this.cities.length ? this.cities : null,
          minPrice: this.priceMin,
          maxPrice: this.priceMax,
          minDays,
          maxDays,
          sortBy: this.sortBy
        }
        const res = await getProductList(body)
        const items = (res.data && res.data.items) || res.data || []
        if (this.pageIndex === 1) {
          this.products = items
        } else {
          this.products.push(...items)
        }
        this.hasMore = items.length >= this.pageSize
      } catch (error) {
        console.error('加载产品列表失败:', error)
      } finally {
        this.loading = false
      }
    },
    loadMore() {
      this.pageIndex++
      this.loadProducts()
    },
    goToProductDetail(product) {
      this.$router.push({
        name: 'ProductDetail',
        params: { id: product.id }
      })
    },
    goToHome() {
      this.$router.push('/')
    }
  }
}
</script>

<style lang="scss" scoped>
.product-list-page {
  width: 100%;
  min-height: 100vh;
  background: #f9fafb;
}

.page-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 10;
}

.back-btn {
  background: none;
  border: none;
  font-size: 15px;
  color: #ff720d;
  padding: 0 8px 0 0;
  cursor: pointer;
}

.page-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  flex: 1;
  text-align: center;
  margin-right: 32px;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}

.search-box {
  flex: 1;
  display: flex;
  gap: 6px;
}

.search-input {
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  font-size: 13px;
  outline: none;
}

.search-btn {
  padding: 8px 14px;
  background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%);
  color: #fff;
  border: none;
  border-radius: 16px;
  font-size: 13px;
  cursor: pointer;
}

.toolbar-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.toolbar-btn {
  position: relative;
  padding: 6px 12px;
  font-size: 13px;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 14px;
  cursor: pointer;
  white-space: nowrap;

  &.active {
    color: #ff720d;
    background: #fff0e3;
  }
}

.toolbar-btn--filter,
.toolbar-btn--filter.active {
  min-width: 56px;
  color: #fff;
  font-weight: 600;
  text-align: center;
  background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%);
  border-radius: 18px;
  box-shadow: 0 3px 8px rgba(255, 114, 13, 0.18);
}

.toolbar-badge {
  display: inline-block;
  margin-left: 4px;
  min-width: 14px;
  height: 14px;
  line-height: 14px;
  border-radius: 7px;
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  text-align: center;
  padding: 0 3px;
}

.filter-groups {
  background: #fff;
  padding: 8px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.filter-group-row {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 6px;

  &:last-child {
    padding-bottom: 0;
  }

  &:not(:last-child) {
    margin-bottom: 4px;
  }
}

.filter-group-tag {
  flex-shrink: 0;
  padding: 5px 12px;
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 12px;
  white-space: nowrap;
  cursor: pointer;

  &.active {
    color: #fff;
    background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%);
  }
}

.chips-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 16px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}

.chip {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  color: #ff720d;
  background: #fff0e3;
  border-radius: 12px;
  cursor: pointer;

  &-reset {
    color: #6b7280;
    background: #f3f4f6;
  }
}

.chip-close {
  font-size: 13px;
}

.product-list-container {
  padding: 16px;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.loading-text,
.no-more-text {
  padding: 20px 16px;
  text-align: center;
  font-size: 13px;
  color: #9ca3af;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 100;
  display: flex;
  align-items: flex-end;
}

.modal-sheet {
  width: 100%;
  background: #fff;
  border-radius: 16px 16px 0 0;
  padding: 16px;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.modal-close {
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
}

.filter-section {
  margin-bottom: 20px;
}

.filter-section-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
}

.price-range-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.price-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
}

.price-sep {
  color: #9ca3af;
}

.option-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.option-tag {
  padding: 6px 14px;
  font-size: 13px;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 14px;
  cursor: pointer;

  &.active {
    color: #fff;
    background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%);
  }
}

.drawer-footer {
  display: flex;
  gap: 12px;
  padding-top: 8px;
}

.drawer-btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &--reset {
    background: #f3f4f6;
    color: #6b7280;
  }

  &--confirm {
    background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%);
    color: #fff;
  }
}

.picker-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.picker-item {
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;

  &.active {
    color: #ff720d;
    border-color: #ff720d;
    background: #fff7f0;
  }
}
</style>
